// src/api.js
const RAW_BASE =
  process.env.VUE_APP_API_URL || "http://127.0.0.1:8000";

const BASE = new URL(
  RAW_BASE.endsWith("/") ? RAW_BASE : RAW_BASE + "/",
  window.location.origin
).toString();

function buildUrl(path) {
  return new URL(
    path.replace(/^\/+/, ""),
    BASE
  ).toString();
}

let onUnauthorized = () => {
  try {
    // limpiar credenciales
    localStorage.removeItem("ac_token");
    localStorage.removeItem("ac_user");

    // guardar a dónde estaba el usuario para volver post-login
    const here = location.pathname + location.search + location.hash;
    if (here !== "/") sessionStorage.setItem("ac_redirect_to", here);
  } catch {}
  // redirigir al login en "/"
  if (location.pathname !== "/") {
    sessionStorage.setItem("ac_unauthorized", 1);
    location.assign("/");
  }
};

export function setOnUnauthorized(fn) {
  if (typeof fn === "function") onUnauthorized = fn;
}

export async function handle(res) {
  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  const data = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => "");

  if (!res.ok) {
    if (res.status === 401) {
      onUnauthorized();
    }

    console.error("HTTP", res.status, data); // <- ESTO debe mostrar detail
    throw new Error(
      `HTTP ${res.status} ${
        typeof data === "string" ? data : JSON.stringify(data)
      }`,
    );
  }
  return data;
}

function getToken() {
  try {
    return localStorage.getItem("ac_token");
  } catch {
    return null;
  }
}

/**
 * Login: envía usuario y password al backend.
 * Espera que el backend responda con { access_token, token_type, user }
 */
export async function apiLogin(usuario, password, opts = {}) {
  const { signal, headers = {} } = opts;
  const url = buildUrl("auth/login");

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...headers,
      },
      body: JSON.stringify({ usuario, password }),
      signal,
    });

    return handle(res);
  } catch (error) {
    // AbortController: no lo transformamos en error de conexión
    if (error?.name === "AbortError") {
      throw error;
    }

    // Los errores HTTP generados por handle() ya tienen mensaje.
    if (error?.message?.startsWith("HTTP ")) {
      throw error;
    }

    // Acá entran errores de red:
    // backend apagado, DNS, conexión rechazada, etc.
    const connectionError = new Error(
      "No se pudo conectar con el servidor."
    );

    connectionError.code = "SERVER_UNAVAILABLE";

    throw connectionError;
  }
}

/**
 * Ejemplo de logout local (opcional)
 */
export function apiLogout() {
  try {
    localStorage.removeItem("ac_token");
    localStorage.removeItem("ac_user");
  } catch {}
}

export async function apiGetClientesBuscar(
  { nombre, skip = 0, limit = 10 },
  opts = {},
) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("clientes/buscar_cliente"));
  const qs = new URLSearchParams({
    ...(nombre ? { nombre } : {}),
    skip: String(skip),
    limit: String(limit),
  });
  url.search = qs.toString();

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", ...headers },
    signal,
  });
  return handle(res);
}

export async function apiGetDeportesActivos(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("deporte/listar_activos"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiGetDeportePorSlug({ slug }, opts = {}) {
  const { signal, headers = {}, credentials } = opts; // credentials: 'include' si tu API usa cookies de sesión
  const url = buildUrl(`deporte/buscar_por_slug/${encodeURIComponent(slug)}`);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    signal,
    // credentials: 'include', // descomenta si tu backend usa cookies/sesión
  });
  return handle(res);
}

export async function apiGetUsuarioInformacion({ idUsuario }, opts = {}) {
  const { signal, headers = {}, credentials } = opts; // credentials: 'include' si tu API usa cookies de sesión
  const url = buildUrl(`usuario/id/${encodeURIComponent(idUsuario)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
    // credentials: 'include', // descomenta si tu backend usa cookies/sesión
  });
  return handle(res);
}

export async function apiGetCanchasPorDeporte({ idDeporte }, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl(
    `cancha/buscar_por_deporte/${encodeURIComponent(idDeporte)}`,
  );

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPostNuevaCancha(
  { idDeporte, descripcion },
  opts = {},
) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl("cancha/");

  const parametros = { id_deporte: idDeporte, descripcion: descripcion };

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(parametros),
    signal,
  });

  return handle(res);
}

export async function apiPutCancha(
  { idDeporte, descripcion, idCancha },
  opts = {},
) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl("cancha/");

  const parametros = {
    id_deporte: idDeporte,
    descripcion: descripcion,
    id_cancha: idCancha,
  };

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(parametros),
    signal,
  });

  return handle(res);
}

export async function apiPostBorrarCancha({ idCancha }, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(
    `cancha/borrar_cancha?id_cancha=${encodeURIComponent(idCancha)}`,
  );

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiGetObtenerCanchaHorarioPrecio(
  { idCancha },
  opts = {},
) {
  const { signal, headers = {} } = opts;

  if (idCancha == null) {
    // si no hay cancha (alta nueva, etc.), devolvemos estructura vacía
    return { configuracionPorDia: [] };
  }

  const url = buildUrl(
    `cancha_horario_precio/obtener-cancha-horario-precio/${encodeURIComponent(
      idCancha,
    )}`,
  );

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPostHorariosPreciosCanchas({ data }, opts = {}) {
  const { signal, headers = {} } = opts;

  if (!data || data.idCancha == null) {
    throw new Error(
      "idCancha es requerido para guardar la configuración de horarios/precios",
    );
  }

  const url = buildUrl("cancha_horario_precio/guardar-cancha-horario-precio");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(data),
    signal,
  });

  return handle(res);
}

export async function apiGetDias(opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("/dia/");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiGetTipoCliente(opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("/tipo_cliente/");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiPostGrabarReserva(data, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("reserva/");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(data),
    signal,
  });

  return handle(res);
}

export async function apiGetReservas(params = {}, opts = {}) {
  const { signal, headers = {} } = opts;

  // 1) Base URL (sin query)
  const baseUrl = buildUrl("/reserva/");

  // 2) Armamos el query string a mano
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.append(key, String(value));
    }
  });

  const query = usp.toString();
  const url = query ? `${baseUrl}?${query}` : baseUrl;

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiGetRangoHorario(params = {}, opts = {}) {
  const { signal, headers = {} } = opts;

  const baseUrl = buildUrl("/cancha_horario_precio/rango-horario");
  const usp = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.append(key, String(value));
    }
  });

  const qs = usp.toString();
  const url = qs ? `${baseUrl}?${qs}` : baseUrl;

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiBuscarCliente(params = {}, opts = {}) {
  const { signal, headers = {} } = opts;

  // Ajustá el path si tu router tiene otro prefijo
  const baseUrl = buildUrl("/cliente/buscar_cliente");
  const usp = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.append(key, String(value));
    }
  });

  const qs = usp.toString();
  const url = qs ? `${baseUrl}?${qs}` : baseUrl;

  const token = getToken();
  const authHeaders = {
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiGetValidarReserva(params = {}, opts = {}) {
  const { signal, headers = {} } = opts;

  const baseUrl = buildUrl("/reserva/validar");
  const usp = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.append(key, String(value));
    }
  });

  const qs = usp.toString();
  const url = qs ? `${baseUrl}?${qs}` : baseUrl;

  const token = getToken();
  const authHeaders = {
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiPutReserva(idReserva, payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`reserva/${encodeURIComponent(idReserva)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetParametroGeneralPorClave(clave, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`parametros_generales/${encodeURIComponent(clave)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetAbonos(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("abono/"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPutAbono(idAbono, payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;
  const url = buildUrl(`abono/${encodeURIComponent(idAbono)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiDeleteAbono(idAbono, opts = {}) {
  const { signal, headers = {}, credentials } = opts;
  const url = buildUrl(`abono/${encodeURIComponent(idAbono)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "DELETE",
    headers: authHeaders,
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetClientes(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("cliente/"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPostCliente(payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`cliente/`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiPutCliente(idCliente, payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`cliente/${encodeURIComponent(idCliente)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiDeleteCliente(idCliente, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`cliente/${encodeURIComponent(idCliente)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "DELETE",
    headers: authHeaders,
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetUsuarios(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("usuario/"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiGetRoles(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("rol/"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPutUsuario(idUsuario, payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`usuario/${encodeURIComponent(idUsuario)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiPostUsuario(payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`usuario/`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiDeleteUsuario(idUsuario, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`usuario/${encodeURIComponent(idUsuario)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "DELETE",
    headers: authHeaders,
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiPutRecuperarUsuario(idUsuario, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`usuario/recuperar/${encodeURIComponent(idUsuario)}`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiPostForgotPassword({ email }, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("auth/forgot-password");

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    body: JSON.stringify({ email }),
    signal,
  });

  return handle(res);
}

export async function apiPostResetPassword({ token, new_password }, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("auth/reset-password");

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    body: JSON.stringify({ token, new_password }),
    signal,
  });

  return handle(res);
}

export async function apiPutChangePassword(payload, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("usuario/password");
  const token = getToken();

  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
  });

  return handle(res);
}

export async function apiPutReservaPagada(idReserva, payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(
    `reserva/actualizar_pagado/${encodeURIComponent(idReserva)}`,
  );

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetParametrosGeneralesObtenerConfigurables(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("parametros_generales/obtener_configurables"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiPutParametrosGenerales(payload, opts = {}) {
  const { signal, headers = {}, credentials } = opts;

  const url = buildUrl(`parametros_generales/actualizar/`);

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
    ...(credentials ? { credentials } : {}),
  });

  return handle(res);
}

export async function apiGetMejorasSistemasUltimas(opts = {}) {
  const { signal, headers = {} } = opts;
  const url = new URL(buildUrl("mejoras_sistemas/ultimas"));

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });
  return handle(res);
}

export async function apiGetFormasPago(opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("/forma_pago/");

  const token = getToken();
  const authHeaders = {
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiGetFormaPagoConfigVigente({ fecha }, opts = {}) {
  const { signal, headers = {} } = opts;

  const baseUrl = buildUrl("/forma_pago/config_vigente");
  const usp = new URLSearchParams({ fecha });
  const url = `${baseUrl}?${usp.toString()}`;

  const token = getToken();
  const authHeaders = {
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders,
    signal,
  });

  return handle(res);
}

export async function apiPostFormaPagoConfig(payload, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("/forma_pago/config");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
  });

  return handle(res);
}

export async function apiCopiarConfiguracionCancha(payload, opts = {}) {
  const { signal, headers = {} } = opts;

  const url = buildUrl("/cancha_horario_precio/copiar-configuracion");

  const token = getToken();
  const authHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders,
    body: JSON.stringify(payload),
    signal,
  });

  return handle(res);
}
export async function apiGetMensajesWhatsApp(opts = {}) {
  const { signal, headers = {} } = opts;
  const token = getToken();
  const res = await fetch(buildUrl('/mensaje_whatsapp/'), { method:'GET', headers:{Accept:'application/json',...headers,...(token?{Authorization:`Bearer ${token}`}:{})}, signal });
  return handle(res);
}

export async function apiGetMensajesWhatsAppActivos(opts = {}) {
  const { signal, headers = {} } = opts;
  const token = getToken();
  const res = await fetch(buildUrl('/mensaje_whatsapp/activos'), { method:'GET', headers:{Accept:'application/json',...headers,...(token?{Authorization:`Bearer ${token}`}:{})}, signal });
  return handle(res);
}

export async function apiPostMensajeWhatsApp(payload, opts = {}) {
  const { signal, headers = {} } = opts; const token=getToken();
  const res=await fetch(buildUrl('/mensaje_whatsapp/'),{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json',...headers,...(token?{Authorization:`Bearer ${token}`}:{})},body:JSON.stringify(payload),signal});
  return handle(res);
}

export async function apiPutMensajeWhatsApp(id, payload, opts = {}) {
  const { signal, headers = {} } = opts; const token=getToken();
  const res=await fetch(buildUrl(`/mensaje_whatsapp/${encodeURIComponent(id)}`),{method:'PUT',headers:{'Content-Type':'application/json',Accept:'application/json',...headers,...(token?{Authorization:`Bearer ${token}`}:{})},body:JSON.stringify(payload),signal});
  return handle(res);
}

export async function apiDeleteMensajeWhatsApp(id, opts = {}) {
  const { signal, headers = {} } = opts; const token=getToken();
  const res=await fetch(buildUrl(`/mensaje_whatsapp/${encodeURIComponent(id)}`),{method:'DELETE',headers:{Accept:'application/json',...headers,...(token?{Authorization:`Bearer ${token}`}:{})},signal});
  return handle(res);
}
